import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { Calendar, Clock, Mountain, Thermometer } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Link } from "@/components/common/Link";
const regions = [{
  id: 1,
  name: "Europe",
  description: "Discover the rich history, diverse cultures, and stunning landscapes of the European continent, from the Mediterranean coast to the Alpine peaks.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/europe_panoramic_landscape.png",
  climate: "Temperate",
  bestTime: "May - Sep",
  difficulty: "Easy to Moderate"
}, {
  id: 2,
  name: "Asia",
  description: "Immerse yourself in the mystic traditions, vibrant cities, and breathtaking natural wonders across the vast Asian continent.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/asia_panoramic_temples.png",
  climate: "Tropical & Varied",
  bestTime: "Oct - Mar",
  difficulty: "Moderate"
}, {
  id: 3,
  name: "South America",
  description: "Embark on wild adventures through dense rainforests, towering mountain ranges, and ancient ruins in South America.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/south_america_panoramic_mountains.png",
  climate: "Tropical & Alpine",
  bestTime: "Sep - Nov",
  difficulty: "Challenging"
}];
const tours = [{
  id: 101,
  regionId: 1,
  title: "Alpine Explorer",
  description: "Journey through the breathtaking Swiss and Italian Alps, experiencing world-class hiking and charming mountain villages.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/tour_alpine_hike.png",
  duration: "7 Days",
  price: "$1,899"
}, {
  id: 102,
  regionId: 1,
  title: "Mediterranean Coast",
  description: "Sail and explore the hidden coves and historic port towns along the stunning Amalfi and Dalmatian coasts.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/tour_mediterranean_coast.png",
  duration: "10 Days",
  price: "$2,450"
}, {
  id: 103,
  regionId: 1,
  title: "Highlands & Castles",
  description: "A rugged journey through the Scottish Highlands, exploring ancient ruins and dramatic landscapes.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/tour_scottish_highlands.png",
  duration: "8 Days",
  price: "$1,600"
}, {
  id: 201,
  regionId: 2,
  title: "Kyoto Heritage",
  description: "Step back in time exploring the ancient temples, bamboo groves, and traditional tea houses of Kyoto.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/tour_kyoto_temples.png",
  duration: "9 Days",
  price: "$2,800"
}, {
  id: 202,
  regionId: 2,
  title: "Himalayan Trek",
  description: "Challenge yourself with a guided trek through the foothills of the Himalayas, experiencing local Sherpa culture.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/tour_himalayan_trek.png",
  duration: "14 Days",
  price: "$3,200"
}, {
  id: 203,
  regionId: 2,
  title: "Mekong River Journey",
  description: "Cruise the mighty Mekong River, discovering floating markets and ancient temples hidden in the jungle.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/tour_mekong_river.png",
  duration: "11 Days",
  price: "$2,100"
}, {
  id: 301,
  regionId: 3,
  title: "Inca Trail to Machu Picchu",
  description: "Hike the legendary Inca Trail through cloud forests and alpine tundra to the lost city of the Incas.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/tour_inca_trail.png",
  duration: "5 Days",
  price: "$1,450"
}, {
  id: 302,
  regionId: 3,
  title: "Patagonia Wilderness",
  description: "Explore the jagged peaks, massive glaciers, and pristine lakes of the Patagonian wilderness.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/tour_patagonia_glacier.png",
  duration: "12 Days",
  price: "$3,800"
}, {
  id: 303,
  regionId: 3,
  title: "Amazon Rainforest Expedition",
  description: "Venture deep into the Amazon basin, spotting exotic wildlife and learning from indigenous communities.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/tour_amazon_rainforest.png",
  duration: "8 Days",
  price: "$2,200"
}];
export default function RegionalHighlights() {
  return <section id="regional-highlights" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 md:mb-16 text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4">
            Explore by Continent
          </h2>
          <p className="text-muted-foreground text-lg">
            Find your next adventure based on the unique landscapes, climates, and cultures of our carefully curated global regions.
          </p>
        </div>

        <div className="flex flex-col gap-16 md:gap-24">
          {regions.map((region, i) => {
          const regionTours = tours.filter(t => t.regionId === region.id);
          return <div key={region.id} data-index={i} className="flex flex-col gap-6 md:gap-8">
                {}
                <div className="relative h-[45vh] min-h-[350px] w-full overflow-hidden rounded-2xl shadow-lg group">
                  <img src={region.image} alt={region.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  
                  <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-10 lg:p-12">
                    <div className="max-w-3xl">
                      <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default text-white mb-4">
                        {region.name}
                      </h3>
                      <p className="text-white/90 mb-6 text-base md:text-lg leading-relaxed">
                        {region.description}
                      </p>
                      
                      <div className="flex flex-wrap gap-3">
                        <Badge variant="secondary" className="bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-md py-1.5 px-3 text-sm font-medium">
                          <Thermometer className="w-4 h-4 mr-1.5 text-primary" />
                          {region.climate}
                        </Badge>
                        <Badge variant="secondary" className="bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-md py-1.5 px-3 text-sm font-medium">
                          <SafeHydrate><Calendar className="w-4 h-4 mr-1.5 text-primary" /></SafeHydrate>
                          {region.bestTime}
                        </Badge>
                        <Badge variant="secondary" className="bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-md py-1.5 px-3 text-sm font-medium">
                          <Mountain className="w-4 h-4 mr-1.5 text-primary" />
                          {region.difficulty}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                {}
                <div className="relative">
                  <ScrollArea className="w-full whitespace-nowrap pb-6 -mx-4 px-4 md:mx-0 md:px-0">
                    <div className="flex w-max space-x-4 md:space-x-6">
                      {regionTours.map((tour, j) => <Card key={tour.id} data-index={j} className="w-[280px] md:w-[350px] shrink-0 flex flex-col overflow-hidden border-border/50 shadow-sm hover:shadow-md transition-all duration-300 bg-card">
                          <div className="relative h-48 md:h-56 w-full overflow-hidden">
                            <img src={tour.image} alt={tour.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-110" />
                            <div className="absolute top-3 right-3 bg-background/90 backdrop-blur-sm px-2.5 py-1 rounded-md text-xs font-bold text-foreground shadow-sm">
                              {tour.price}
                            </div>
                          </div>
                          
                          <CardHeader className="px-5 pt-5 pb-2 whitespace-normal">
                            <CardTitle className="text-xl font-default text-foreground line-clamp-1">
                              {tour.title}
                            </CardTitle>
                            <CardDescription className="line-clamp-2 mt-2 text-sm leading-relaxed">
                              {tour.description}
                            </CardDescription>
                          </CardHeader>
                          
                          <CardContent className="px-5 py-3 flex-grow">
                            <div className="flex items-center text-sm text-muted-foreground font-medium">
                              <Clock className="w-4 h-4 mr-2 text-primary" />
                              {tour.duration}
                            </div>
                          </CardContent>
                          
                          <CardFooter className="px-5 pb-5 pt-0">
                            <Button asChild variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                              <Link to="#">
                                View Itinerary
                              </Link>
                            </Button>
                          </CardFooter>
                        </Card>)}
                    </div>
                    <ScrollBar orientation="horizontal" className="hidden md:flex" />
                  </ScrollArea>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}